'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import { CheckIcon } from 'lucide-react';
import { useCallback } from 'react';
import { DataGrid, Badge, Spinner } from '@repo/components';
import GenericDataPageHeader from 'src/components/GenericDataPageHeader';
import { TaskTemplate, typeSchema, Type, TypesApiResponse, TaskTemplatesApiResponse } from '@repo/shared/domain';
import {
	guardedPost,
	sortToString,
	stringToSort,
	constructQuery,
	guardedClient as clientRequest,
} from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import { getAllEventTypes } from 'src/api/admin/types';
import { getTaskTemplates } from 'src/api/admin/taskTemplates';

interface EventTypesSearch {
	sort?: string;
	page?: number;
}

export const Route = createFileRoute('/_admin/config/types/event/')({
	validateSearch: (search: Record<string, unknown>): EventTypesSearch => ({
		sort: search.sort as string | undefined,
		page: search.page as number | undefined,
	}),
	component: EventTypesPage,
});

function EventTypesPage() {
	const searchParams = Route.useSearch();
	const { sort, page } = searchParams;
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const updateQueryParams = useUpdateQueryParams();

	useBreadcrumbLevel(0, 'Configuration');
	useBreadcrumbLevel(1, 'Event Types');

	const query = constructQuery({
		page,
		sort: sort?.includes('Form.title') ? `form:${sort.split(':')[1]}` : sort || 'name:asc',
		noPagination: 'false',
	});

	const { data: typesData, isLoading: typesLoading } = useQuery({
		queryKey: ['types-event', query],
		queryFn: () => getAllEventTypes(query),
	});

	const taskTemplatesQuery = constructQuery({
		sort: 'name:asc',
		type: 'event,approval',
		noPagination: 'true',
	});

	const { data: taskTemplatesData } = useQuery({
		queryKey: ['task-templates-for-event-types'],
		queryFn: () => getTaskTemplates(taskTemplatesQuery),
	});

	const types = typesData || { data: [], count: 0, page: 0, pageSize: 25 };
	const taskTemplates: TaskTemplate[] = taskTemplatesData?.data || [];

	const columns = [
		{
			field: 'name',
			headerName: 'Name',
			flex: 1,
		},
		{
			field: 'Form.title',
			headerName: 'Form',
			flex: 1,
		},
		{
			field: 'tasks',
			headerName: 'Tasks',
			flex: 2,
			renderCell: (c: { value: string[] }) =>
				c.value?.map((r) => {
					const t = taskTemplates.find((task) => r === task.slug);
					return t ? <Badge key={r}>{t.name}</Badge> : null;
				}),
			sortable: false,
		},
		{
			field: 'requirements',
			headerName: 'Requirements',
			flex: 2,
			renderCell: (c: { value: { id: string; name: string }[] }) =>
				c.value?.map((r) => <Badge key={r.id}>{r.name}</Badge>),
			sortable: false,
		},
		{
			field: 'inheritUsersFromGroups',
			headerName: 'Access Management',
			flex: 1,
			renderCell: (c: { value: boolean }) => (c.value ? 'Company-based' : 'User-based'),
		},
		{
			field: 'allowChildGroups',
			headerName: 'Subcontracting',
			flex: 1,
			renderCell: (c: { value: boolean }) => (c.value ? <CheckIcon /> : ''),
		},
	];

	const handleAddEventType = useCallback(async () => {
		const response = await guardedPost<Type>('/api/types/event', {});

		if (response.body?.id) {
			navigate({ to: '/config/types/event/$typeId', params: { typeId: response.body.id } });
		}
	}, [navigate]);

	const handleSort = ([sortItem]: { field: string; sort: 'asc' | 'desc' }[]) => {
		if (!sortItem) {
			return;
		}
		updateQueryParams({ sort: sortToString(sortItem) });
	};

	const handlePaginationModelChange = ({ page: newPage }: { page: number }) => {
		updateQueryParams({ page: newPage });
	};

	const handleRowClick = ({ row }: { row: Type }) => {
		navigate({ to: '/config/types/event/$typeId', params: { typeId: row.id } });
	};

	const revalidateTypes = () => {
		queryClient.invalidateQueries({ queryKey: ['types-event'] });
	};

	const actions = [
		<Button key="add" onClick={handleAddEventType} variant="outlined" startIcon={<AddIcon />}>
			Add Event Type
		</Button>,
	];

	return (
		<div>
			<GenericDataPageHeader actions={actions} />

			<div className="p-4">
				<Paper variant="outlined">
					<DataGrid
						data={types?.data || []}
						rowCount={types?.count || 0}
						page={types?.page || 0}
						pageSize={types?.pageSize || 0}
						columns={columns}
						isLoading={typesLoading}
						sortModel={[stringToSort(sort || 'name:asc')]}
						onSortModelChange={handleSort}
						onReload={revalidateTypes}
						onPaginationModelChange={handlePaginationModelChange}
						paginationMode="server"
						sortingMode="server"
						sortingOrder={['asc', 'desc']}
						onRowClick={handleRowClick}
					/>
				</Paper>
			</div>
		</div>
	);
}
